import { useStaff, useUpdateThread } from '@zuruflex/api-client/react';
import type { SlaTargets } from '@zuruflex/core';
import { formatDateIn, toLocale } from '@zuruflex/i18n';
import {
  THREAD_PRIORITIES,
  THREAD_SETTABLE_STATUSES,
  THREAD_TOPICS,
  type MessageThreadDetail,
  type ThreadPriority,
  type ThreadStatus,
  type ThreadTopic,
} from '@zuruflex/types';
import {
  Badge,
  Button,
  Combobox,
  describeError,
  Label,
  CsatScoreMark,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SlaIndicator,
} from '@zuruflex/ui';
import { Check, Link2, Pencil, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { EditThreadDialog } from './EditThreadDialog.js';
import { SnoozeMenu } from './SnoozeMenu.js';

/**
 * The statuses the plain status control offers.
 *
 * `SNOOZED` is still not among them, and the reason has changed. It used
 * to be absent because nothing woke a snoozed thread up: the API refuses
 * the status without a `snoozedUntil`, there was no control here that
 * asked for a date, and a thread put down stayed down for good. The sweep
 * (`POST /v1/threads/sweep`, on cron) fixed the second half of that — a
 * snoozed thread now comes back on its own — so snoozing is reachable,
 * through `SnoozeMenu` below.
 *
 * It stays off *this* control because a `Select` cannot ask for a date.
 * `PATCH /v1/threads/:id` wants the status and the instant in one request,
 * and the menu is what sends both together. Picking "Snoozed" here would
 * be a request the API is right to refuse.
 */
const OFFERED_STATUSES = THREAD_SETTABLE_STATUSES.filter((status) => status !== 'SNOOZED');

/** Sentinels for the two rows pinned above the colleagues. Never a real id. */
const ASSIGN_ME = '@me';
const UNASSIGN = '@none';

export interface ContextPaneProps {
  thread: MessageThreadDetail;
  /** From the metrics response, never from `GET /v1/settings` — that is MANAGER-only. */
  targets: SlaTargets | null;
}

/** Who is asking, about what, and every lever an agent has over it. */
export function ContextPane({ thread, targets }: ContextPaneProps) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  /** `null` is idle, `true` is the tick, `false` is the browser having refused. */
  const [copied, setCopied] = useState<boolean | null>(null);
  /*
   * Cleared on unmount. An agent who copies a link and moves to the next
   * thread inside two seconds would otherwise have `setCopied` called on a
   * component that is gone.
   */
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (settle.current) clearTimeout(settle.current);
    },
    [],
  );

  /**
   * The whole URL, filters and all: the link is the desk this thread was
   * found in, which is what a handover note needs.
   *
   * `navigator.clipboard` is absent on any non-secure origin and rejects
   * wherever the permission is denied, and both used to end here in
   * silence — the icon never changed, nothing was copied, and the handover
   * note went out with whatever was on the clipboard before. A refusal now
   * says so with the same mark it would have confirmed with.
   */
  const copyLink = useCallback(() => {
    const mark = (ok: boolean) => {
      setCopied(ok);
      if (settle.current) clearTimeout(settle.current);
      settle.current = setTimeout(() => setCopied(null), 2000);
    };

    const write = navigator.clipboard?.writeText(window.location.href);
    if (!write) {
      mark(false);
      return;
    }
    write.then(
      () => mark(true),
      () => mark(false),
    );
  }, []);

  const update = useUpdateThread(api, thread.id);
  const staff = useStaff(api);

  const colleagues = (staff.data?.items ?? []).filter((member) => member.isActive);

  const assigneeOptions = [
    { value: ASSIGN_ME, label: t('inbox.claim') },
    { value: UNASSIGN, label: t('inbox.unassigned') },
    ...colleagues.map((member) => ({
      value: member.id,
      label: member.fullName,
      description: member.email,
    })),
  ];

  const support = thread.kind === 'SUPPORT';

  return (
    <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto rounded-card border border-border-default bg-surface p-4">
      <div>
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
          {t('inbox.who')}
        </h3>
        <p className="mt-1 text-sm font-semibold">{thread.with.name}</p>
        {thread.with.ratingCount ? (
          <p className="text-xs text-text-muted">
            {t('common:rating.summary', {
              value: (thread.with.ratingAverage ?? 0) / 100,
              count: thread.with.ratingCount,
            })}
          </p>
        ) : null}
      </div>

      {/*
        "Currently about" rather than "Booking": a renter↔owner thread is
        one conversation carrying every hire the two of them have had, and
        this is whichever one it has most recently been pointed at. The
        earlier ones are in the transcript, behind a divider each.
      */}
      {thread.booking ? (
        <div>
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
            {/*
              `common:field.booking` does not exist in the catalogue — the
              old page asked for it and rendered the key. What is under the
              heading is the reference, which does.
            */}
            {thread.kind === 'DIRECT' ? t('inbox.subject') : t('common:field.reference')}
          </h3>
          <Link
            to={`/bookings/${thread.booking.reference}`}
            className="mt-1 block text-sm underline underline-offset-2"
          >
            {thread.booking.reference}
          </Link>
          <p className="text-xs text-text-muted">
            {t(`common:bookingStatus.${thread.booking.status}`)}
          </p>
        </div>
      ) : null}

      {thread.vehicle ? (
        <div>
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
            {t('common:field.vehicle')}
          </h3>
          <Link
            to={`/fleet/${thread.vehicle.slug}`}
            className="mt-1 block text-sm underline underline-offset-2"
          >
            {thread.vehicle.make} {thread.vehicle.model} {thread.vehicle.year}
          </Link>
        </div>
      ) : null}

      {support ? (
        <div className="space-y-3">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
            {t('inbox.handling')}
          </h3>

          {/*
            Both clocks, because they fail for different reasons and an
            agent deciding what to pick up next needs to know which one is
            running. Targets come from the metrics response — an ordinary
            agent cannot read `GET /v1/settings` and would get a 403.

            The pause is `SlaIndicator`'s own — it asks `slaState` and draws
            nothing on a snoozed, resolved or closed thread, so this no
            longer has to remember the gate independently of the badge.
          */}
          {targets ? (
            <SlaIndicator
              thread={thread}
              firstResponseTargetMinutes={targets.firstResponseTargetMinutes}
              resolutionTargetHours={targets.resolutionTargetHours}
              labels={{
                firstResponse: t('inbox.sla.firstResponse'),
                resolution: t('inbox.sla.resolution'),
                ok: t('inbox.sla.met'),
                // `inbox.sla.due` is written as "Due {{time}}" and the
                // indicator supplies its own elapsed figure, so what is
                // wanted here is the state's name with nothing after it.
                due: t('inbox.sla.due', { time: '' }).trim(),
                breached: t('inbox.sla.breached'),
              }}
            />
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="thread-status">{t('common:field.status')}</Label>
            <Select
              value={thread.status}
              onValueChange={(value) => update.mutate({ status: value as ThreadStatus })}
            >
              <SelectTrigger id="thread-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OFFERED_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(`inbox.status.${status}`)}
                  </SelectItem>
                ))}
                {/*
                  Shown, never chosen: a thread that is asleep has to be
                  able to say so in the control that names its status.
                */}
                {thread.status === 'SNOOZED' ? (
                  <SelectItem value="SNOOZED" disabled>
                    {t('inbox.status.SNOOZED')}
                  </SelectItem>
                ) : null}
              </SelectContent>
            </Select>
          </div>

          <SnoozeMenu
            snoozedUntil={thread.snoozedUntil}
            disabled={update.isPending}
            // Never one without the other: `PATCH /v1/threads/:id` refuses
            // SNOOZED with no date, and OPEN is what cancels one.
            onSnooze={(until) =>
              update.mutate({ status: 'SNOOZED', snoozedUntil: until.toISOString() })
            }
            onWake={() => update.mutate({ status: 'OPEN' })}
          />

          <div className="space-y-1.5">
            <Label htmlFor="thread-priority">{t('inbox.filter.priority')}</Label>
            <Select
              value={thread.priority}
              onValueChange={(value) => update.mutate({ priority: value as ThreadPriority })}
            >
              <SelectTrigger id="thread-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THREAD_PRIORITIES.map((priority) => (
                  <SelectItem key={priority} value={priority}>
                    {t(`inbox.priority.${priority}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="thread-topic">{t('inbox.filter.topic')}</Label>
            <Select
              value={thread.topic}
              onValueChange={(value) => update.mutate({ topic: value as ThreadTopic })}
            >
              <SelectTrigger id="thread-topic">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/*
                  Every topic, unfiltered. `SUPPORT_TOPICS_FOR` narrows what
                  a *requester* may file under; the desk refiles into any of
                  them, including the one a renter is not offered.
                */}
                {THREAD_TOPICS.map((topic) => (
                  <SelectItem key={topic} value={topic}>
                    {t(`inbox.topic.${topic}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="thread-assignee">{t('inbox.assignee')}</Label>
            <Combobox
              id="thread-assignee"
              options={assigneeOptions}
              value={thread.assignee?.id ?? UNASSIGN}
              placeholder={t('inbox.assigneePlaceholder')}
              searchPlaceholder={t('common:action.search')}
              emptyMessage={t('inbox.savedReplyEmpty')}
              disabled={update.isPending}
              onChange={(value) => {
                // `me` is a *filter* the list understands, not an id the
                // API will accept — so claiming is assigning your own id.
                const assigneeId =
                  value === ASSIGN_ME ? (user?.id ?? null) : value === UNASSIGN ? null : value;
                update.mutate({ assigneeId });
              }}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label>{t('common:support.subject')}</Label>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-3.5" aria-hidden />
                {t('common:action.change')}
              </Button>
            </div>
            <p className="text-sm text-text">{thread.subject || '—'}</p>
            {thread.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {thread.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>

          {/*
            The result, never the prompt. Admin shows what the requester
            said about the desk; asking the desk to rate itself would be
            the one thing the score cannot mean.
          */}
          <div className="space-y-1">
            <Label>{t('inbox.csat.score')}</Label>
            {thread.csat?.score ? (
              <div className="flex items-center gap-2">
                {/* The figure, not the stars a car is rated with — see
                    `CsatScoreMark`. The desk reads the same mark the
                    customer was shown. */}
                <CsatScoreMark
                  score={thread.csat.score}
                  label={t(`common:csat.score.${thread.csat.score}`)}
                />
                {thread.csat.comment ? (
                  <p className="text-xs text-text-muted">“{thread.csat.comment}”</p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-text-muted">{t('inbox.csat.none')}</p>
            )}
          </div>

          {/* A control that silently does nothing is worse than an error. */}
          {update.isError ? (
            <p className="text-xs text-danger">{describeError(update.error)}</p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-auto space-y-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={copyLink}
        >
          {/*
            The confirmation is the icon rather than a second label: there
            is no "Copied" in the catalogue, and an English one here would
            be the only untranslated string on the page. A refusal is the
            same trick in reverse — the danger-coloured cross is as
            wordless as the tick and says the opposite.
          */}
          {copied === true ? (
            <Check className="size-3.5" aria-hidden />
          ) : copied === false ? (
            <X className="size-3.5 text-danger" aria-hidden />
          ) : (
            <Link2 className="size-3.5" aria-hidden />
          )}
          {t('inbox.deepLink')}
        </Button>

        <p className="text-xs text-text-muted">
          {t('inbox.opened', { date: formatDateIn(thread.createdAt, locale) })}
        </p>
      </div>

      <EditThreadDialog
        thread={thread}
        open={editing}
        onOpenChange={setEditing}
        pending={update.isPending}
        error={update.isError ? update.error : undefined}
        onSave={(patch) => update.mutateAsync(patch)}
      />
    </aside>
  );
}
